import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";

const Section = ({ num, title, children }) => (
  <section className="space-y-3" data-testid={`policy-section-${num}`}>
    <h2 className="text-lg sm:text-xl font-bold text-white">
      <span className="text-[#00cccc] font-mono text-sm mr-2">{num}.</span>{title}
    </h2>
    <div className="text-sm sm:text-[15px] text-zinc-400 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Sub = ({ letter, title, children }) => (
  <div>
    <h3 className="text-sm font-semibold text-zinc-200 mt-4 mb-1.5">{letter}. {title}</h3>
    {children}
  </div>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — Biker Boulevard";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="privacy-page" className="relative bg-[#0D0F12] text-white min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} />
      </div>
      <Navbar />
      <div className="pt-28 pb-24 max-w-3xl mx-auto px-5 md:px-8">
        <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Legal</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight" data-testid="policy-title">Privacy Policy</h1>
        <p className="mt-2 text-xs font-mono text-zinc-500">Effective Date: 10 October 2026</p>

        <div className="mt-8 space-y-4 text-sm sm:text-[15px] text-zinc-400 leading-relaxed">
          <p>
            Biker Boulevard (&ldquo;BB&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) respects your privacy and is committed to protecting the personal information you share with us through our website, platform, and related services.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect your information when you use Biker Boulevard to explore, buy, sell, or list pre-owned motorcycles.
          </p>
        </div>

        <div className="mt-10 space-y-8">
          <Section num="1" title="Information We Collect">
            <p>When you use Biker Boulevard, we may collect the following information:</p>
            <Sub letter="A" title="Personal Information">
              <Ul items={["Full name", "Email address", "Phone number and WhatsApp number", "City, state, and location", "Other contact details you voluntarily provide"]} />
            </Sub>
            <Sub letter="B" title="Google Sign-In Information">
              <p>
                We allow users to sign in using their Google accounts. When you choose Google Sign-In, we receive the information authorized by you through Google&apos;s authentication process, such as your name, email address, and profile information where applicable.
              </p>
              <p>
                We do not ask for or collect your Google account password. Google&apos;s handling of your information is also subject to Google&apos;s own privacy policies.
              </p>
            </Sub>
            <Sub letter="C" title="Motorcycle and Listing Information">
              <p>When you list a motorcycle, we may collect:</p>
              <Ul items={[
                "Brand, model, variant, and year of registration",
                "Registration number and other vehicle identification details you provide",
                "Kilometres driven and vehicle condition",
                "Expected selling price",
                "Photographs and videos of the motorcycle",
                "Location of the motorcycle",
                "Loan, challan, ownership, or other vehicle-related details you provide",
              ]} />
            </Sub>
            <Sub letter="D" title="Technical Information">
              <p>
                We may collect limited technical information such as device details, browser information, IP address, usage activity, and cookies, where applicable, to operate, secure, and improve our platform.
              </p>
            </Sub>
          </Section>

          <Section num="2" title="How We Use Your Information">
            <p>We use your information to:</p>
            <Ul items={[
              "Create and manage your account.",
              "Publish and manage motorcycle listings.",
              "Connect interested buyers and sellers.",
              "Provide motorcycle valuation estimates.",
              "Communicate about listings, enquiries, and platform services.",
              "Improve our website, valuation tools, and user experience.",
              "Detect fraud, prevent misuse, and maintain platform security.",
              "Comply with applicable legal and regulatory requirements.",
            ]} />
            <p>
              We will not use your personal information for unrelated purposes without an appropriate legal basis or consent where required.
            </p>
          </Section>

          <Section num="3" title="Public Listings and Confidential Information">
            <p>
              Biker Boulevard is a marketplace, so certain information submitted with a listing may be visible to other users or the public. This may include motorcycle photographs, make, model, year, kilometres driven, asking price, and general location.
            </p>
            <p>
              Your private account information, such as your email address, phone number, and personal details, is not intended to be displayed publicly by default. However, information you choose to include in listing descriptions, photographs, or messages may become visible to others.
            </p>
            <p>
              Please avoid uploading sensitive personal documents or information that is not necessary for your listing.
            </p>
          </Section>

          <Section num="4" title="Sharing of Information">
            <p>
              We treat your personal information as confidential and do not sell your personal information to third parties.
            </p>
            <p>We may share limited information when reasonably necessary with:</p>
            <Ul items={[
              "Technology, hosting, database, authentication, and security providers that help us operate the platform.",
              "Buyers or sellers, to the extent necessary to facilitate a transaction or respond to an enquiry.",
              "Professional advisers or service providers bound by appropriate confidentiality obligations.",
              "Government authorities, law enforcement agencies, or other parties where disclosure is required by law or necessary to protect legal rights and platform security.",
            ]} />
            <p>We aim to limit disclosures to what is reasonably necessary for the relevant purpose.</p>
          </Section>

          <Section num="5" title="Data Storage and Security">
            <p>
              We use reasonable technical and organizational measures designed to protect your information against unauthorized access, alteration, disclosure, loss, or misuse.
            </p>
            <p>
              Access to personal information is restricted where appropriate. However, no website, online service, or electronic storage system can be guaranteed to be completely secure.
            </p>
            <p>
              We retain information only for as long as reasonably necessary for the purposes described in this policy, including account management, transactions, dispute resolution, security, and legal compliance.
            </p>
          </Section>

          <Section num="6" title="Cookies and Similar Technologies">
            <p>
              Our website may use cookies or similar technologies to maintain sessions, support account functionality, understand website usage, and improve performance. You can manage cookies through your browser settings, although disabling certain cookies may affect website functionality.
            </p>
          </Section>

          <Section num="7" title="Your Rights and Choices">
            <p>Subject to applicable law, you may request to:</p>
            <Ul items={[
              "Access or correct your personal information.",
              "Update your account details.",
              "Remove or deactivate your motorcycle listing.",
              "Withdraw consent where processing relies on consent.",
              "Request deletion of personal information, subject to legal retention requirements.",
              "Raise a concern about how your information is handled.",
            ]} />
            <p>
              To make a request, contact us using the details below. We may need to verify your identity before acting on your request.
            </p>
          </Section>

          <Section num="8" title="Third-Party Services">
            <p>
              Our platform may use third-party services, including Google Sign-In and hosting or infrastructure providers. These services may process information in accordance with their own privacy policies and applicable agreements.
            </p>
            <p>
              Biker Boulevard is not responsible for the independent privacy practices of third-party websites or services that we do not control.
            </p>
          </Section>

          <Section num="9" title="Children's Privacy">
            <p>
              Biker Boulevard is intended for individuals legally capable of entering into the relevant transactions. If you believe a child has provided personal information to us inappropriately, please contact us so that we can review the matter.
            </p>
          </Section>

          <Section num="10" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy as our services, technology, or legal obligations evolve. The revised version will be published on our website with an updated effective date where appropriate.
            </p>
          </Section>

          <Section num="11" title="Contact Us">
            <p>For privacy questions, requests, or concerns, please contact:</p>
            <div className="mt-2 p-4 rounded-xl glass" data-testid="policy-contact">
              <p className="text-white font-semibold">Biker Boulevard</p>
              <p>Email: <a href="mailto:reachbikerblvd@gmail.com" className="text-[#00cccc] hover:underline">reachbikerblvd@gmail.com</a></p>
              <p>Location: Lucknow, Uttar Pradesh, India</p>
            </div>
          </Section>
        </div>

        <p className="mt-12 text-xs text-zinc-500 border-t border-white/10 pt-6">
          By using our platform, you acknowledge that you have read this Privacy Policy. Where consent is legally required, we will seek it through an appropriate process.
        </p>
      </div>
      <Footer />
    </main>
  );
}
